import React, { useEffect, useRef, useState } from 'react'
import { Select } from '../../../ui'
import { useStore } from '../../../StateProvider'
import { equals, map } from 'ramda'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { fetchPlatforms } from '../../api'
import { usePammRefId, useParamsObj, usePresetValue } from '../../utils/hooks'
import { getUserValue } from '../utils'
import styles from './styles.css'
import queryString from 'query-string'
import { CREATE_MASTER_ACCOUNT } from '../../../constants/routes'

const ExternalSelectPlatform = ({ label, name, additionalInfo, showLabelAsPlaceholder }) => {
  const { t, i18n } = useTranslation()
  const [value, setValue] = useState('')
  const [fetchError, setFetchError] = useState('')
  const [platformsOptions, setPlatformsOptions] = useState([])
  const [isValid, setIsValid] = useState(null)
  const [{
    user,
    user: { brokerGroup, utmCampaign, deptCode, fundId },
    form: { accId },
    whitelabel: { targetPlatform }
  }, dispatch] = useStore()
  const location = useLocation()
  const userValue = getUserValue(accId, user, name)
  const paramsObj = useParamsObj(
    { brokerGroup, utmCampaign, deptCode, fundId, search: location.search })
  const valueRef = useRef('')
  const { fund_id: queryFundId = '' } = queryString.parse(location.search)
  const visibleRef = useRef(true)
  const pammRef = usePammRefId()

  const presetValue = (value) => {
    setValue(value)
    valueRef.current = value
    const isValid = value.length > 0
    setIsValid(isValid)
    dispatch({ type: 'setExtFieldValue', name, value })
    dispatch({ type: 'setValidationFld', name, value: isValid })
  }

  useEffect(() => {
    if (queryFundId.length || pammRef) {
      visibleRef.current = false
      dispatch({ type: 'removeFieldValue', name })
      dispatch({ type: 'removeValidationFld', name })
    } else if (!visibleRef.current) {
      visibleRef.current = true
      dispatch({ type: 'addValidationFld', name })
      presetValue(valueRef.current)
    }
  }, [queryFundId, pammRef])

  usePresetValue(name, userValue, presetValue)

  useEffect(() => {
    if (visibleRef.current) {
      const params = location.pathname === CREATE_MASTER_ACCOUNT
        ? {
          ...paramsObj,
          isPamm: true
        } : paramsObj

      fetchPlatforms(params).then(
      /**
        * @param {{platforms: string[]}} platformsResponse
       */
        ({ platforms }) => {
          setPlatformsOptions(platforms.map((platform) => ({
            value: platform, label: t(`generatedField.${platform}`)
          })))
          setFetchError('')

          if (platforms.length === 1) {
            dispatch({ type: 'setExtFieldValue', name, value: platforms[0] })
            dispatch({ type: 'setValidationFld', name, value: true })
          } else if (valueRef.current) {
            if (!platforms.includes(valueRef.current)) {
              presetValue('')
            } else {
              presetValue(valueRef.current)
            }
          } else if (!valueRef.current && targetPlatform && platforms.includes(targetPlatform)) {
            presetValue(targetPlatform)
          }
        }, (error) => {
          setFetchError(error.message)
          presetValue('')
        })
    }
  }, [i18n.language])

  /**
   * @param {string} value
   */
  const getSelectValue = (value) => (equals(value, t('generatedField.choose')) ? '' : value)

  const onChange = ({ target: { value, name } }) => {
    const selectedValue = getSelectValue(value)
    setValue(selectedValue)
    valueRef.current = selectedValue
    setIsValid(!!selectedValue.length)

    dispatch({ type: 'setExtFieldValue', name, value: selectedValue })
    dispatch({ type: 'setValidationFld', name, value: !!selectedValue.length })
  }

  if (!visibleRef.current) {
    return null
  }

  return (
    <>
      {platformsOptions.length > 1 ? (
        <Select
          value={value || ''}
          label={t(label)}
          placeholder={t('generatedField.choose')}
          options={platformsOptions}
          onChange={onChange}
          name={name}
          key={name}
          error=''
          valid={isValid}
          info={additionalInfo ? t(`generatedField.${name}AdditionalInfo`) : ''}
          showLabelAsPlaceholder={showLabelAsPlaceholder}
        />
      ) : null}
      {fetchError.length > 0 && <span className={styles.error}>{fetchError}</span>}
    </>
  )
}

const getExternalPlatformSelectFields = map(platformField => {
  const { i18n, name, required, order } = platformField
  return (
    <ExternalSelectPlatform
      label={i18n ? `generatedField.${i18n}` : ''}
      name={name}
      required={required}
      key={name}
      order={order}
    />
  )
})

export { ExternalSelectPlatform, getExternalPlatformSelectFields }
